import { Icon } from '@iconik-io/uad';
import {
  UPLOADER_STATUS_COMPLETED,
  UPLOADER_STATUS_FAILED,
  UPLOADER_STATUS_UPLOADING,
  UPLOADER_STATUS_WAITING,
  type UploaderState,
} from '@iconik-io/uploader/src/state';

import {
  UPLOADER_RENAME_FILE_MODAL,
  UPLOADER_RETRY,
} from '../../constants/modals';
import { selectUploadItem } from '../../ducks/uploader/selectors';
import {
  getUploadedBytes,
  isFileExistsFailure,
  isPausedUpload,
} from '../../ducks/uploader/upload_state';
import { useAppSelector } from '../../store/react_redux_typed';
import ProgressPie from '../../ui/progress_pie/progress_pie';
import { filesize } from '../../utils/filesize';
import ErrorButton from './error_button';

import styles from './uploads_panel_widget.module.css';

export type FileStatusProps = {
  item: UploaderState;
};

export function FileStatus({ item }: FileStatusProps) {
  const uploadItem = useAppSelector((state) =>
    selectUploadItem(state, item.id),
  );
  const loaded = getUploadedBytes(item);
  const total = item.fileInfo.size;
  let status;
  if (isFileExistsFailure(item, uploadItem)) {
    status = (
      <ErrorButton
        text="File already exists. Rename"
        item={item}
        modal={UPLOADER_RENAME_FILE_MODAL}
      />
    );
  } else if (item.status === UPLOADER_STATUS_FAILED) {
    status = (
      <ErrorButton
        text={uploadItem?.error?.message ?? 'Upload failed. Retry'}
        item={item}
        modal={UPLOADER_RETRY}
      />
    );
  }
  if (item.status === UPLOADER_STATUS_UPLOADING || isPausedUpload(item)) {
    status = (
      <div className={styles.progress}>
        <div className={styles.progress_label}>
          {filesize(loaded)}
          <br />
          of&nbsp;
          <span className={styles.highlight}>{filesize(total)}</span>
        </div>
        <ProgressPie
          showLabel={false}
          size={18}
          value={loaded && total ? Math.round((loaded / total) * 100) : 0}
        />
      </div>
    );
  }
  if (item.status === UPLOADER_STATUS_COMPLETED) {
    status = <Icon name="checkmark" className={styles.success} />;
  }
  if (!status) {
    status = (
      <Icon
        name="pending"
        className={
          item.status === UPLOADER_STATUS_WAITING
            ? styles.queued
            : styles.staged
        }
      />
    );
  }
  return <div className={styles.status}>{status}</div>;
}
